import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
  homeHref?: string;
}

// Route to label mapping
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  clients: 'Clients',
  deals: 'Deals',
  quotes: 'Quotes',
  tasks: 'Tasks',
  chat: 'Chat',
  catalogue: 'Catalogue',
  training: 'Training',
  admin: 'Admin',
  analytics: 'Analytics',
  users: 'Users',
  suppliers: 'Suppliers',
  inventory: 'Inventory',
  companies: 'Companies',
  pipelines: 'Pipelines',
  segments: 'Segments',
  templates: 'Email Templates',
  emails: 'Email History',
  new: 'New',
  edit: 'Edit',
  videos: 'Videos',
  progress: 'Progress',
  salesmen: 'Salesmen',
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
  showHome = true,
  homeHref = '/dashboard',
}) => {
  const location = useLocation();

  // Auto-generate breadcrumbs from current path if items not provided
  const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => {
    if (items) return items;

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const generatedItems: BreadcrumbItem[] = [];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Skip numeric IDs, but include them as part of the path
      if (/^\d+$/.test(segment)) {
        // For ID segments, modify the previous item to include the ID in its path
        if (generatedItems.length > 0) {
          generatedItems[generatedItems.length - 1].href = currentPath;
        }
        return;
      }

      const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      const isLast = index === pathSegments.length - 1;

      generatedItems.push({
        label,
        href: isLast ? undefined : currentPath,
      });
    });

    return generatedItems;
  }, [items, location.pathname]);

  if (breadcrumbItems.length === 0) return null;

  return (
    <nav className={clsx('flex items-center', className)} aria-label="Breadcrumb">
      <motion.ol
        className="flex items-center space-x-2" // Increased space
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Home link */}
        {showHome && (
          <motion.li
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            <Link
              to={homeHref}
              className={clsx(
                'flex items-center justify-center w-8 h-8 rounded-lg',
                'bg-white/60 backdrop-blur-sm border border-neutral-200/60', // Glass base
                'text-neutral-500 hover:text-blue-600',
                'hover:bg-white hover:border-blue-200 hover:shadow-md hover:-translate-y-0.5', // Hover effect
                'transition-all duration-300'
              )}
            >
              <HomeIcon className="h-4 w-4" />
              <span className="sr-only">Home</span>
            </Link>
          </motion.li>
        )}

        {/* Breadcrumb items */}
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <motion.li
              key={index}
              className="flex items-center"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + index * 0.05, duration: 0.2 }}
            >
              <ChevronRightIcon className="h-3 w-3 text-neutral-400 mx-2" strokeWidth={2.5} />
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium',
                    'text-neutral-600 hover:text-blue-700',
                    'bg-white/50 backdrop-blur-sm border border-transparent', // Subtle glass
                    'hover:bg-white hover:border-blue-100 hover:shadow-sm hover:-translate-y-0.5',
                    'transition-all duration-300'
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-semibold',
                    // Gradient Active Item
                    'text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md shadow-blue-500/20',
                    'border border-blue-500/20'
                  )}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
            </motion.li>
          );
        })}
      </motion.ol>
    </nav>
  );
};

export default Breadcrumbs;
